import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supplierApi } from "../../api";
import { ProcError, ProcForm, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";
import type { SupplierStatus } from "../../types";

export default function SupplierEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: supplier, loading: loadingSupplier, error: loadError } = useAsyncData(
    () => supplierApi.get(Number(id)),
    [id],
  );

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [kraPin, setKraPin] = useState("");
  const [vatCertNo, setVatCertNo] = useState("");
  const [prequalified, setPrequalified] = useState(false);
  const [approvedDate, setApprovedDate] = useState("");
  const [status, setStatus] = useState<SupplierStatus>("PENDING");

  useEffect(() => {
    if (supplier) {
      setCode(supplier.code);
      setName(supplier.name);
      setCategory(supplier.category);
      setContactPerson(supplier.contactPerson || "");
      setPhone(supplier.phone || "");
      setEmail(supplier.email || "");
      setAddress(supplier.address || "");
      setKraPin(supplier.kraPin || "");
      setVatCertNo(supplier.vatCertNo || "");
      setPrequalified(supplier.prequalified);
      setApprovedDate(supplier.approvedDate || "");
      setStatus(supplier.status);
    }
  }, [supplier]);

  const { mutate, loading, error } = useMutation(async () => {
    if (!code.trim()) throw new Error("Code is required.");
    if (!name.trim()) throw new Error("Name is required.");
    if (!category.trim()) throw new Error("Category is required.");
    await supplierApi.update(Number(id), {
      code,
      name,
      category,
      contactPerson,
      phone,
      email,
      address: address || null,
      kraPin: kraPin || null,
      vatCertNo: vatCertNo || null,
      prequalified,
      approvedDate: prequalified && approvedDate ? approvedDate : null,
      status,
    });
    navigate(`/procurement/suppliers/${id}`);
  });

  if (loadingSupplier) return <ProcLoading />;
  if (loadError) return <ProcError message={loadError} />;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to={`/procurement/suppliers/${id}`} className="text-sm text-emerald-700 hover:underline">&larr; Back to Supplier</Link>
      </div>

      <ProcForm formNo="PROC-FRM-10" title="Supplier Registration">
        {error && <ProcError message={error} />}

        <ProcSection title="Supplier Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Code *</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Category *</label>
              <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. IT Equipment, Office Supplies, Construction" className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Contact Person</label>
              <input type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Phone</label>
              <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Address</label>
              <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">KRA PIN</label>
              <input type="text" value={kraPin} onChange={(e) => setKraPin(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">VAT Certificate No</label>
              <input type="text" value={vatCertNo} onChange={(e) => setVatCertNo(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as SupplierStatus)} className="w-full rounded-xl border px-3 py-2 text-sm">
                <option value="PENDING">PENDING</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="SUSPENDED">SUSPENDED</option>
                <option value="DELISTED">DELISTED</option>
              </select>
            </div>
            <div className="flex items-center gap-3 pt-5">
              <input id="prequalified" type="checkbox" checked={prequalified} onChange={(e) => setPrequalified(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-emerald-600" />
              <label htmlFor="prequalified" className="text-sm font-semibold text-slate-700">Prequalified</label>
            </div>
            {prequalified && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Approval Date</label>
                <input type="date" value={approvedDate} onChange={(e) => setApprovedDate(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
              </div>
            )}
          </div>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to={`/procurement/suppliers/${id}`} className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="button" onClick={() => mutate()} disabled={loading} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
            {loading ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
